// src/config.js - 등급 시스템 및 다국어 지원 추가 + 구독 시스템

import { getConfigNumber } from './services/remoteConfig'; // [추가] Remote Config import

// 이미지 경로들을 관리하는 객체입니다.
export const imagePaths = {
  loginBackground: 'https://placehold.co/1080x1920/1a1a2e/ffffff',
  painoriLogo: 'https://placehold.co/400x150/ffffff/1a1a2e?text=PAINORI',
  piIcon: 'https://placehold.co/32x32/f0ad4e/ffffff?text=Pi',
};

// 🔧 [STAR 포인트 설정] 광고 시청 시 획득 가능한 STAR 포인트 (Remote Config 연동)
export const getAdWatchPpPoints = () => {
  return getConfigNumber('ad_watch_pp_points', 0); // 기본값 0, Remote Config 필수
};

export const getAdWatchSpotRewards = () => {
  return getConfigNumber('ad_watch_spot_points'); // [Strict] 기본값 제거
};

// 특정 기능들을 켜고 끄는 데 사용되는 객체입니다.
export const features = {
  enableGuestLogin: true,
};

// STAR 구매 가격 계산 설정 (USD는 내부 계산용이며 UI에는 표시하지 않음)
// functions/index.js의 STAR_PURCHASE_PRICING과 반드시 동기화 유지
export const STAR_PURCHASE = {
  USD_PER_STAR: 0.001,
  FEE_MULTIPLIER: 1.08,
  PI_DECIMALS: 4
};

// [추가] 구독 타입 정의
export const SUBSCRIPTION_TYPES = {
  DAILY: {
    id: '1d',
    nameKey: 'subscription_daily',
    displayName: '1일권',
    get price() { return getConfigNumber('subscription_price_daily'); }, // [Strict] 기본값 제거
    duration: 1,
    features: {
      removeAds: true,
      bonusHints: 0,
      scoreBonus: 0
    }
  },
  WEEKLY: {
    id: '7d',
    nameKey: 'subscription_weekly',
    displayName: '7일권',
    get price() { return getConfigNumber('subscription_price_weekly'); }, // [Strict] 기본값 제거
    duration: 7,
    discount: 55, // 55% 할인
    features: {
      removeAds: true,
      bonusHints: 0,
      scoreBonus: 0
    }
  },
  PREMIUM: {
    id: '30d',
    nameKey: 'subscription_premium',
    displayName: '30일 프리미엄',
    get price() { return getConfigNumber('subscription_price_premium'); }, // [Strict] 기본값 제거
    duration: 30,
    features: {
      removeAds: true,
      bonusHints: 1, // 힌트 +1
      scoreBonus: 0.1 // 점수 10% 보너스
    }
  }
};

// [추가] 구독 타입 헬퍼 함수
export const getSubscriptionType = (typeId) => {
  return Object.values(SUBSCRIPTION_TYPES).find(type => type.id === typeId);
};

// [추가] 구독 혜택 확인 함수
export const getSubscriptionBenefits = (subscription) => {
  if (!subscription || !subscription.type) return null;

  const type = getSubscriptionType(subscription.type);
  if (!type) return null;

  // 만료 시간 확인
  if (new Date(subscription.expiresAt) <= new Date()) return null;

  return type.features;
};

// 등급 시스템 정의 - 100억 점 기준으로 확장 (방안 2)
export const tierSystem = [
  { name: 'Iron', nameKey: 'tier_iron', minScore: 0, color: '#B8B8B8' },
  { name: 'Bronze', nameKey: 'tier_bronze', minScore: 1000000, color: '#cd7f32' },
  { name: 'Silver', nameKey: 'tier_silver', minScore: 5000000, color: '#c0c0c0' },
  { name: 'Gold', nameKey: 'tier_gold', minScore: 15000000, color: '#ffd700' },
  { name: 'Platinum', nameKey: 'tier_platinum', minScore: 40000000, color: '#e5e4e2' },
  { name: 'Diamond', nameKey: 'tier_diamond', minScore: 100000000, color: '#b9f2ff' },
  { name: 'Master', nameKey: 'tier_master', minScore: 250000000, color: '#ff69b4' },
  { name: 'Grandmaster', nameKey: 'tier_grandmaster', minScore: 500000000, color: '#ff1493' },
  { name: 'Challenger', nameKey: 'tier_challenger', minScore: 750000000, color: '#ff0000' },
  { name: 'Legend', nameKey: 'tier_legend', minScore: 1000000000, color: '#ffa500' },
  { name: 'Mythic', nameKey: 'tier_mythic', minScore: 2500000000, color: '#8A2BE2' },
  { name: 'Immortal', nameKey: 'tier_immortal', minScore: 5000000000, color: '#FFD700' },
  { name: 'Transcendent', nameKey: 'tier_transcendent', minScore: 7500000000, color: '#FF69B4' },
  { name: 'Supreme', nameKey: 'tier_supreme', minScore: 10000000000, color: '#FF4500' }
];

// 누적 점수로 현재 등급 계산
export const getTierByScore = (score) => {
  for (let i = tierSystem.length - 1; i >= 0; i--) {
    if (score >= tierSystem[i].minScore) {
      return tierSystem[i];
    }
  }
  return tierSystem[0]; // Iron
};

// [수정] 환경별 테마 설정 가져오기 - sandbox/production으로 변경
export const getThemeConfigByEnvironment = (environment) => {
  const isSandbox = environment === 'sandbox' || environment === 'testnet';

  return {
    masterpiece_1: {
      price: 0,
      isLocked: false,
      isComingSoon: false
    },
    masterpiece_2: {
      get price() { return isSandbox ? 1 : getConfigNumber('theme_price_masterpiece_2', 0.314); }, // [수정] Remote Config 연동으로 유료화 - 특별 가격
      get isLocked() {
        const price = isSandbox ? 1 : getConfigNumber('theme_price_masterpiece_2', 0.314);
        return price > 0; // 🔧 가격이 0이면 자동으로 무료로 전환
      },
      isComingSoon: isSandbox ? true : false
    },
    masterpiece_3: {
      get price() { return isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_3', 3.14); }, // [수정] getter로 변경
      get isLocked() {
        const price = isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_3', 3.14);
        return price > 0; // 🔧 가격이 0이면 자동으로 무료로 전환
      },
      isComingSoon: isSandbox ? true : false
    },
    masterpiece_4: {
      get price() { return isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_4', 3.14); }, // [수정] getter로 변경
      get isLocked() {
        const price = isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_4', 3.14);
        return price > 0; // 🔧 가격이 0이면 자동으로 무료로 전환
      },
      isComingSoon: isSandbox ? true : false
    },
    masterpiece_5: {
      get price() { return isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_5', 3.14); }, // [수정] getter로 변경
      get isLocked() {
        const price = isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_5', 3.14);
        return price > 0; // 🔧 가격이 0이면 자동으로 무료로 전환
      },
      isComingSoon: isSandbox ? true : false
    },
    masterpiece_6: {
      get price() { return isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_6', 3.14); }, // [수정] getter로 변경
      get isLocked() {
        const price = isSandbox ? 0 : getConfigNumber('theme_price_masterpiece_6', 3.14);
        return price > 0; // 🔧 가격이 0이면 자동으로 무료로 전환
      },
      isComingSoon: isSandbox ? true : false
    }
  };
};

// 🔧 배포 실패 해결: themeConfig 변수 정의 추가
const themeConfig = getThemeConfigByEnvironment('production');

// 게임의 핵심 데이터를 관리하는 객체입니다.
export const gameData = {
  // 테마 정의 (3개 테마) - nameKey 추가로 다국어 지원
  themes: [
    {
      id: 'masterpiece_1',
      nameKey: 'theme_masterpiece_1',
      displayName: '명화 1', // 기본값 (번역이 없을 때 사용)
      startStageId: 10000,
      endStageId: 10009,
      isLocked: false, // 무료 제공
      price: 0,
      description: '고전 명화 컬렉션 1'
    },
    {
      id: 'masterpiece_2',
      nameKey: 'theme_masterpiece_2',
      displayName: '명화 2',
      startStageId: 10010,
      endStageId: 10019,
      get isLocked() { return themeConfig.masterpiece_2.isLocked; }, // [수정] 유료화로 잠금
      get price() { return themeConfig.masterpiece_2.price; }, // [수정] Remote Config 연동
      description: '고전 명화 컬렉션 2'
    },
    {
      id: 'masterpiece_3',
      nameKey: 'theme_masterpiece_3',
      displayName: '명화 3',
      startStageId: 10020,
      endStageId: 10029,
      get isLocked() { return themeConfig.masterpiece_3.isLocked; }, // 🔧 themeConfig 연동
      get price() { return themeConfig.masterpiece_3.price; }, // 🔧 themeConfig 연동
      description: '고전 명화 컬렉션 3'
    },
    {
      id: 'masterpiece_4',
      nameKey: 'theme_masterpiece_4',
      displayName: '명화 4',
      startStageId: 10030,
      endStageId: 10039,
      get isLocked() { return themeConfig.masterpiece_4.isLocked; }, // 🔧 themeConfig 연동
      get price() { return themeConfig.masterpiece_4.price; }, // 🔧 themeConfig 연동
      description: '고전 명화 컬렉션 4'
    },
    {
      id: 'masterpiece_5',
      nameKey: 'theme_masterpiece_5',
      displayName: '명화 5',
      startStageId: 10040,
      endStageId: 10049,
      get isLocked() { return themeConfig.masterpiece_5.isLocked; }, // 🔧 themeConfig 연동
      get price() { return themeConfig.masterpiece_5.price; }, // 🔧 themeConfig 연동
      description: '고전 명화 컬렉션 5'
    },
    {
      id: 'masterpiece_6',
      nameKey: 'theme_masterpiece_6',
      displayName: '명화 6',
      startStageId: 10050,
      endStageId: 10059,
      get isLocked() { return themeConfig.masterpiece_6.isLocked; }, // 🔧 themeConfig 연동
      get price() { return themeConfig.masterpiece_6.price; }, // 🔧 themeConfig 연동
      description: '고전 명화 컬렉션 6'
    }
  ],

  // 모든 스테이지 정보 (30개)
  stages: [],
};

// stages 배열 자동 생성 (30개 스테이지)
for (let themeIndex = 0; themeIndex < 6; themeIndex++) {
  const theme = gameData.themes[themeIndex];

  for (let stageOffset = 0; stageOffset < 10; stageOffset++) {
    const stageId = theme.startStageId + stageOffset;

    gameData.stages.push({
      id: stageId,
      themeId: theme.id,
      packKey: theme.nameKey,
      stageLevel: stageOffset + 1, // 1~10
      get timeLimit() { return getConfigNumber('game_time_default', 60); }, // [수정] getter로 변경
      imageWidth: 480,
      imageHeight: 340,
      totalDifferences: 5,
    });
  }
}

// 헬퍼 함수: 테마 ID로 해당 테마의 모든 스테이지 ID 가져오기
export const getStageIdsByTheme = (themeId) => {
  const theme = gameData.themes.find(t => t.id === themeId);
  if (!theme) return [];

  const stageIds = [];
  for (let i = theme.startStageId; i <= theme.endStageId; i++) {
    stageIds.push(i);
  }
  return stageIds;
};

// 헬퍼 함수: 테마에서 랜덤으로 스테이지 선택
export const getRandomStagesFromTheme = (themeId, count = 5) => {
  const allStageIds = getStageIdsByTheme(themeId);
  const shuffled = [...allStageIds].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

// 헬퍼 함수: 잠금 해제된 모든 테마에서 랜덤으로 스테이지 선택
export const getRandomStagesFromUnlockedThemes = (count = 10) => {
  const unlockedThemes = gameData.themes.filter(theme => !theme.isLocked);
  let allStageIds = [];

  unlockedThemes.forEach(theme => {
    const stageIds = getStageIdsByTheme(theme.id);
    allStageIds = [...allStageIds, ...stageIds];
  });

  const shuffled = [...allStageIds].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

export const THEMES_PER_PAGE = 9;